"use client";

import React from "react";
import Link from "next/link";
import clsx from "clsx";

/**
 * "More" action button ported from the v1 site
 * (assets/js/fetch.js → format_advisories, and .resources-white-paper__button
 * in style.css — both reuse the same .button/.button--small atom): a dark pill
 * with a red outline, an offset gray ring, a pink accent on the right edge, and
 * a small gray tick at the bottom-right. Fills red on hover.
 *
 * Shared by the Security Advisories table and the White Paper list so the two
 * Resources pages stay identical.
 */
interface MoreButtonProps {
  href: string;
  /** Button label. v1 used "More" (advisories) / "MORE" (white paper). */
  label?: string;
  /** Pass "_blank" for external links (e.g. PDF downloads). */
  target?: "_blank";
  onClick?: (e: React.MouseEvent) => void;
  /** Extra classes on the root (e.g. `self-start` inside a flex column). */
  className?: string;
}

export default function MoreButton({
  href,
  label = "More",
  target,
  onClick,
  className,
}: MoreButtonProps) {
  const cls = clsx("ecq-more-btn", className);
  const inner = (
    <>
      <span className="text">{label}</span>
      <span className="b-gray" aria-hidden />
      <span className="b-pink" aria-hidden />
      <span className="b-gray-tiny" aria-hidden />
    </>
  );

  return (
    <>
      {target === "_blank" ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          className={cls}
          aria-label={label}
        >
          {inner}
        </a>
      ) : (
        <Link href={href} onClick={onClick} className={cls} aria-label={label}>
          {inner}
        </Link>
      )}

      <style jsx global>{`
        /* Dimensions match v1 .button.button--small (style.css): 5px padding,
           115px min-width, 40px line-height, 16px text, 37px radius. */
        .ecq-more-btn {
          position: relative;
          display: inline-block;
          padding: 5px;
          min-width: 115px;
          text-decoration: none;
          vertical-align: middle;
        }
        .ecq-more-btn .text {
          display: block;
          position: relative;
          z-index: 4;
          /* Horizontal breathing room so longer labels (e.g. "View details")
             don't touch the pill's rounded edge / pink cap. Short labels stay
             centred inside the 115px min-width, so this doesn't affect them.
             Extra right padding clears the ~75px pink cap on the right edge. */
          padding: 0 28px;
          border-radius: 37px;
          border: 1px solid #ec1938;
          background: #16191e;
          color: #ffffff;
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 1.78px;
          line-height: 40px;
          text-align: center;
          transition: background 0.4s, color 0.4s;
        }
        .ecq-more-btn:hover .text {
          background: #ec1938;
          color: #ffffff;
        }
        .ecq-more-btn .b-gray {
          position: absolute;
          inset: 0;
          z-index: 1;
          border: 1px solid #272a2e;
          border-radius: 37px;
        }
        .ecq-more-btn .b-pink {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 2;
          width: 75px;
          height: 100%;
          border: 1px solid #ff002d;
          border-left: none;
          border-top-right-radius: 37px;
          border-bottom-right-radius: 37px;
        }
        .ecq-more-btn .b-gray-tiny {
          position: absolute;
          right: 35px;
          bottom: 0;
          z-index: 3;
          width: 45px;
          border-bottom: 1px solid #272a2e;
        }
      `}</style>
    </>
  );
}
